import {
  EmptyStateButton,
  Prompt,
  SnackbarStatus,
  Table,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import styles from './PriceSchemeChannelRulesTab.module.scss';
import {
  CHANNEL_AUTOMATIONS,
  CHANNEL_AUTOMATIONS_DESCRIPTION,
  CREATE_CHANNEL_RULE,
  CREATE_CHANNEL_FIRST,
  GO_TO_ADMIN,
} from './constants';
import EmptyState from '../EmptyState/EmptyState';
import Burger from '../../assets/images/Burger';
import { useState } from 'react';
import { PromptActions, IntegrationType } from 'src/constants';
import { useRules } from 'src/hooks/useRules/useRules';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { ChannelRuleModal } from '../ChannelRuleModal';
import { getChannelRulesTableColumns } from './Table';
import SkeletonContainer from '../SkeletonContainer';
import { IAggregator } from 'src/types/aggregator.types';
import { ChannelRuleSteps } from '../ChannelRuleModal/ChannelRuleModalContent';
import { deletePriceRule } from 'src/api/deletePriceRule/deletePriceRule';
import { useQueryClient } from '@tanstack/react-query';
import { PRICE_RULES_QUERY } from 'src/hooks/QueryKeys';

const ruleEntityFilter = (system: IAggregator) =>
  system.aggregatorId !== null && system.integrationType === IntegrationType.DELIVERY_SERVICE;

type TEditPriceBand = {
  channel: string | null;
  step: number | null;
};

const DEFAULT_EDITING_STATE = {
  channel: null,
  step: null,
};

export const PriceSchemeChannelRulesTab = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [editingChannelRule, setEditingChannelRule] = useState<TEditPriceBand>(DEFAULT_EDITING_STATE);
  const [deletingChannelRule, setDeletingChannelRule] = useState<string>(null);

  const { data: externalSystems, isLoading: isExternalSystemsLoading } = useExternalSystems();
  const { data: priceRules = [], isLoading: isRulesLoading } = useRules(priceSchemeId);
  const isLoadingData = isRulesLoading || isExternalSystemsLoading;

  const ruleEntities = externalSystems?.filter(ruleEntityFilter) || [];
  const usedChannelIds = new Set(
    priceRules.filter((rule) => rule.external_system?.id).map((rule) => rule.external_system.id),
  );
  const allChannelsInUse = ruleEntities.every((entity) => usedChannelIds.has(entity.aggregatorId));

  const channelRules = priceRules.length > 0 ? priceRules.filter((rule) => rule.external_system) : [];
  channelRules.sort((row1, row2) => row1.display_order - row2.display_order);

  const handleDelete = async (ruleId: string) => {
    try {
      await deletePriceRule(ruleId, priceSchemeId);
      await queryClient.invalidateQueries({ queryKey: PRICE_RULES_QUERY });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: 'Price rule deleted successfully',
      });
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Failed to delete price rule',
      });
    } finally {
      setDeletingChannelRule(null);
    }
  };
  const tableRows = priceRules.filter((rule) => rule.external_system?.id);

  const handleCreateClick = () => setEditingChannelRule({ step: ChannelRuleSteps.Channels, channel: null });
  const handleEditClick = (channel: string, step: ChannelRuleSteps) => setEditingChannelRule({ channel, step });
  const handleDeleteClick = (channelId: string) => setDeletingChannelRule(channelId);

  const columns = getChannelRulesTableColumns({
    setModalOpen: handleCreateClick,
    handleEdit: handleEditClick,
    handleDelete: handleDeleteClick,
    allChannelsInUse,
  });

  const createChannelRule = () => setEditingChannelRule({ channel: null, step: ChannelRuleSteps.Channels });
  const resetEditingChannelRule = () => setEditingChannelRule(DEFAULT_EDITING_STATE);

  const ruleEntitiesExist = ruleEntities && ruleEntities.length > 0;

  const deleteChannelRulePromptOpen = deletingChannelRule !== null;

  const activeChannel = channelRules.find((rule) => rule.price_rule_id === editingChannelRule.channel);

  if (isLoadingData) {
    return <SkeletonContainer hiddenTitle />;
  }

  return (
    <>
      <Prompt
        promptHeading={'Are you sure you want to delete this channel rule?'}
        closePrompt={() => setDeletingChannelRule(null)}
        open={deleteChannelRulePromptOpen}
        buttonHandler={() => handleDelete(deletingChannelRule)}
        buttonText={PromptActions.DELETE}
        backButtonText={PromptActions.CANCEL}
        backButton={true}
        size="small"
      >
        You will permanently remove the calculation and rounding rules assigned to this channel.
      </Prompt>

      <div className={styles['rules-list']}>
        {tableRows.length > 0 ? (
          <Table
            data={tableRows}
            columns={columns}
            columnHeadersToRightAlign={['adjustmentValue', 'roundingValue', 'actions']}
          />
        ) : (
          <div>
            <Typography size="xlarge" weight="semi-bold">
              {CHANNEL_AUTOMATIONS}
            </Typography>
            <Typography className={styles['description']}>{CHANNEL_AUTOMATIONS_DESCRIPTION}</Typography>
            {ruleEntitiesExist ? (
              <EmptyStateButton label={CREATE_CHANNEL_RULE} onClick={createChannelRule} />
            ) : (
              <EmptyState
                emptySectionText={CREATE_CHANNEL_FIRST}
                onClick={() => globalThis.location.assign(globalThis.location.origin.replace('hub', 'admin'))}
                buttonText={GO_TO_ADMIN}
                Icon={Burger}
              />
            )}
          </div>
        )}
      </div>
      <ChannelRuleModal
        priceRuleToEdit={activeChannel}
        priceSchemeId={priceSchemeId}
        open={editingChannelRule.step !== null}
        step={editingChannelRule.step}
        closeModal={resetEditingChannelRule}
      />
    </>
  );
};
